import { useEffect, useState } from "react";
import {
  Tabs,
  Tab,
  Box,
  SwipeableDrawer,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Description,
  AutoAwesome,
  Insights,
  Tune,
  Close,
  LockOutlined,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import Documents from "./Documents";
import PropertiesDrawer from "./PropertiesDrawer";
import ChatWithPdf from "./ChatWithPdf";
import AIInsightsPanel from "./AIInsightsPanel";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { setMobilePropertiesRequested } from "redux/appSlice";
import { RootState } from "utils/store";
import strings from "global/constants/StringConstants";

const DocumentTabMobile = ({
  signRequest,
  docLoading,
  isMobile,
}: {
  signRequest: any;
  docLoading: boolean;
  isMobile?: boolean;
}) => {
  const [value, setValue] = useState<number | false>(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const dispatch = useAppDispatch();
  const { mobilePropertiesRequested } = useAppSelector(
    (state: RootState) => state.app,
  );

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setDrawerOpen(true);
  };

  const handleClose = () => {
    setDrawerOpen(false);
    setValue(false);
  };

  useEffect(() => {
    if (!isMobile) return;
    if (!mobilePropertiesRequested) return;

    setValue(1);
    setDrawerOpen(true);
    dispatch(setMobilePropertiesRequested(false));
  }, [mobilePropertiesRequested, isMobile, dispatch]);

  const renderDrawerContent = () => {
    switch (value) {
      case 0:
        return (
          <Documents docLoading={docLoading} onFileChange={handleClose} />
        );
      case 1:
        return <PropertiesDrawer onClose={handleClose} isMobile={isMobile} />;
      case 2:
        return <ChatWithPdf signRequest={signRequest} />;
      case 3:
        return <AIInsightsPanel hideHeader signRequest={signRequest} />;
      default:
        return null;
    }
  };

  const aiState = useFeatureState(FeatureKeys.AI_ASSISTANT);
  const aiAvailable = aiState.status === strings.FEATURE_STATUS_AVAILABLE;
  const aiTooltip =
    aiState.status === "not-in-plan"
      ? "Upgrade your plan to access AI Assistant"
      : aiState.status === "admin-disabled"
        ? (aiState.disabledMessage ?? "AI Assistant is temporarily unavailable")
        : "";

  const tabLabels = ["Documents", "Properties", "AI Assistant", "Insights"];

  return (
    <>
      <Box
        sx={{
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
          borderRadius: "10px",
          boxShadow: "0px 1px 4px rgba(0,0,0,0.1)",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons={true}
          allowScrollButtonsMobile
          aria-label="mobile tabs"
          sx={{
            minHeight: "30px",
            "& .MuiTab-root": {
              minHeight: "30px",
              padding: "2px 8px",
              fontSize: "12px",
              textTransform: "none",
              marginLeft: "0px",
              color: "#646464",
              fontWeight: 400,
            },
            "& .MuiTab-root.Mui-selected": {
              color: "#111827",
              fontWeight: 600,
            },
            "& .MuiTabs-indicator": {
              display: "none",
            },
          }}
        >
          <Tab
            icon={<Description sx={{ fontSize: "14px" }} />}
            iconPosition="start"
            label="Documents"
          />
          <Tab
            icon={<Tune sx={{ fontSize: "14px" }} />}
            iconPosition="start"
            label="Properties"
          />
          <Tooltip title={aiTooltip} disableHoverListener={aiAvailable} arrow>
            <Box component="span">
              <Tab
                icon={
                  aiAvailable ? (
                    <AutoAwesome sx={{ fontSize: "14px" }} />
                  ) : (
                    <LockOutlined sx={{ fontSize: "14px" }} />
                  )
                }
                iconPosition="start"
                label="AI Assistant"
                disabled={!aiAvailable}
              />
            </Box>
          </Tooltip>
          <Tooltip title={aiTooltip} disableHoverListener={aiAvailable} arrow>
            <Box component="span">
              <Tab
                icon={
                  aiAvailable ? (
                    <Insights sx={{ fontSize: "14px" }} />
                  ) : (
                    <LockOutlined sx={{ fontSize: "14px" }} />
                  )
                }
                iconPosition="start"
                label="Insights"
                disabled={!aiAvailable}
              />
            </Box>
          </Tooltip>
        </Tabs>
      </Box>
      <SwipeableDrawer
        anchor="bottom"
        open={drawerOpen}
        onOpen={() => setDrawerOpen(true)}
        onClose={handleClose}
        disableSwipeToOpen
        sx={{
          "& .MuiDrawer-paper": {
            borderTopLeftRadius: "16px",
            borderTopRightRadius: "16px",
            height: "90vh",
            overflow: "hidden",
          },
        }}
      >
        {/* Drag handle */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            pt: 1,
            pb: 0.5,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 2,
              backgroundColor: "#E0E0E0",
            }}
          />
        </Box>

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1,
            borderBottom: "1px solid #F0F0F0",
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
            {value !== false ? tabLabels[value] : ""}
          </Typography>
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label={`Close ${value !== false ? tabLabels[value] : ""} panel`.trim()}
          >
            <Close sx={{ fontSize: "18px" }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", height: "100%" }}>
          {renderDrawerContent()}
        </Box>
      </SwipeableDrawer>
    </>
  );
};

export default DocumentTabMobile;
